import {Injectable, OnDestroy, inject} from '@angular/core';

import {ReplaySubject} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  takeUntil,
} from 'rxjs/operators';

import * as PL2 from '@common/utils/dist/models/models-pl2.js';
import {KeysPL2 as K} from '@common/utils/dist/models/keys-pl2.js';

import {SendAgentMessageAction} from '@actions/send-agent-message.action';
import {ActiveConnectionDataViewStore} from '@stores/active-connection-data-view.store';
import {AgentMemoryStore, SessionState} from '@stores/agent-memory.store';
import {AttentionStore} from '@stores/attention.store';
import {ConnectionStore} from '@stores/connection.store';
import {DataConfigStore} from '@stores/data-config.store';
import {EntryStore} from '@stores/entry.store';
import {RouteStore} from '@stores/route.store';
import {UserStore} from '@stores/user.store';
import {ChatMessage, QuickReply, StatusIcon} from '@models/chat-message-types';
import {CheckpointType} from '@models/trial-data-window-types';
import {lessonChatSessionId} from '@utils/ai-utils';
import {attentionKind} from '@utils/attention-context-utils';
import {DataConfigStateUtils as DCSU} from '@utils/data-config-state-utils';
import {LocalSubsetUtils as LSU} from '@utils/local-subset-utils';
import {isSessionPanelOpen} from '@utils/route-utils';

const CHECKPOINT_ICONS: Partial<Record<CheckpointType, StatusIcon>> = {
  device_connected: 'device',
  trial_started: 'recording-started',
  recording_stopped: 'recording-stopped',
  trial_saved: 'info',
};

/** The status line for a checkpoint: only the connection and the saved trial name the device */
function checkpointText(type: CheckpointType, deviceName?: string): string {
  switch (type) {
    case 'device_connected':
      return deviceName
        ? `Device connected - ${deviceName}`
        : 'Device connected';
    case 'trial_started':
      return 'Recording started';
    case 'recording_stopped':
      return 'Recording stopped';
    case 'trial_saved':
      return `Trial saved${deviceName ? ` (${deviceName})` : ''}`;
    default:
      return type;
  }
}

/** Shown under the welcome message until the teacher picks one or types */
const WELCOME_QUICK_REPLIES: QuickReply[] = [
  {
    label: 'Check my setup',
    prompt:
      'Check my setup. Review the sensors I have selected, the data rate and the graphs being shown. ' +
      'Flag anything that could be an issue for this lesson, and offer to help troubleshoot.',
  },
  {
    label: 'Explain this data',
    prompt:
      'Explain this data. Describe what each line on the graph shows, the typical values, and how I should ' +
      'expect them to change with motion. If I am viewing a recording, point out the specific patterns in it.',
  },
  {
    label: 'Questions for students',
    prompt:
      'Suggest questions I could ask students that help them interpret this data.',
  },
];

/** After connecting, the chat says hello unless a recording starts first */
const WELCOME_DELAY_MS = 8000;

/**
 * Follows data collection for the lesson chat: connecting a device or recording on a data card
 * moves the user's attention to it, saving a trial moves it to the new trial card. Each step is
 * noted in the open chat, which is also told about the attention change (otherwise the next
 * message carries it).
 *
 * The live data is the app-wide DataConfigStore (saved trials' views have their own), watched
 * only for data status changes; the connection is the active connection data view.
 */
@Injectable({providedIn: 'root'})
export class LessonChatReactor implements OnDestroy {
  private _activeDataViewStore = inject(ActiveConnectionDataViewStore);
  private _attentionStore = inject(AttentionStore);
  private _connectionStore = inject(ConnectionStore);
  private _dataConfigStore = inject(DataConfigStore);
  private _entryStore = inject(EntryStore);
  private _memoryStore = inject(AgentMemoryStore);
  private _routeStore = inject(RouteStore);
  private _sendAction = inject(SendAgentMessageAction);
  private _userStore = inject(UserStore);

  private _destroyed$ = new ReplaySubject<void>();
  /** When the current recording started (0: none seen), to tell a save from a clear */
  private _recordingStartedAt = 0;
  private _welcomeTimer: ReturnType<typeof setTimeout> | undefined;
  private _welcomed = false;
  /** Document statuses last seen, by chat session and document id */
  private _documentStatuses = new Map<string, string>();

  constructor() {
    this._activeDataViewStore.state$
      .pipe(
        distinctUntilChanged(),
        filter((cardPK): cardPK is string => !!cardPK),
        takeUntil(this._destroyed$),
      )
      .subscribe((cardPK) => this._connected(cardPK));

    this._dataConfigStore.state$
      .pipe(
        map((state) => DCSU.dataStatus(state ?? {})),
        distinctUntilChanged(),
        pairwise(),
        takeUntil(this._destroyed$),
      )
      .subscribe(([previous, status]) => this._statusChanged(previous, status));

    this._memoryStore.state$
      .pipe(takeUntil(this._destroyed$))
      .subscribe((state) => this._documentsChanged(state?.sessions ?? {}));
  }

  ngOnDestroy() {
    clearTimeout(this._welcomeTimer);
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  /** Says so in that chat when a document finishes processing or fails */
  private _documentsChanged(sessions: Record<string, SessionState>) {
    for (const [sid, session] of Object.entries(sessions)) {
      for (const doc of session.agentContext?.documents ?? []) {
        const key = `${sid}/${doc.docId}`;
        const previous = this._documentStatuses.get(key);
        this._documentStatuses.set(key, doc.status);
        if (previous !== 'processing') {
          continue;
        }
        if (doc.status === 'ready') {
          this._addDocumentLine(sid, doc.docId, {
            content: 'Document ready:',
            statusLink: {docId: doc.docId, text: doc.name},
          });
        } else if (doc.status === 'failed') {
          this._addDocumentLine(sid, doc.docId, {
            content: `Could not read ${doc.name}.`,
          });
        }
      }
    }
  }

  /**
   * Deferred: this runs inside the memory store's own state$ emission, and adding the message
   * synchronously would deliver the new state first and then this older one to later subscribers.
   */
  private _addDocumentLine(
    sid: string,
    docId: string,
    line: Pick<ChatMessage, 'content' | 'statusLink'>,
  ) {
    queueMicrotask(() =>
      this._memoryStore.addMessage(sid, {
        id: `${Date.now()}-document-${docId}`,
        role: 'system',
        isStreaming: false,
        statusIcon: 'document',
        ...line,
      }),
    );
  }

  private _connected(cardPK: string) {
    if (!this._attendLiveData(cardPK)) return;
    this._note('device_connected');
    this._sendAttention();
    this._scheduleWelcome();
  }

  private _statusChanged(
    previous: PL2.DataStatus | undefined,
    status: PL2.DataStatus | undefined,
  ) {
    if (status === PL2.DataStatus.Record) {
      this._recordingStartedAt = Date.now();
      clearTimeout(this._welcomeTimer);
      this._welcomed = true;
      const cardPK = this._activeDataViewStore.state();
      if (cardPK && this._attendLiveData(cardPK)) this._sendAttention();
      this._note('trial_started');
    } else if (
      previous === PL2.DataStatus.Record &&
      status === PL2.DataStatus.Recorded
    ) {
      this._note('recording_stopped');
    } else if (
      previous === PL2.DataStatus.Recorded &&
      status === PL2.DataStatus.Setup
    ) {
      // A save clears the card once the trial is created; the Clear button clears it too
      const trial = this._trialSavedSinceRecording();
      if (trial) {
        this._attentionStore.attend(trial.pK, trial.name, 'trial', 'save');
        this._note('trial_saved');
        this._sendAttention();
      }
    }
  }

  /** False when the card isn't a lesson data card (e.g. the trials page's data view) */
  private _attendLiveData(cardPK: string): boolean {
    const entry = this._entryStore.state()[cardPK]?.e;
    if (attentionKind(entry?.t) !== 'live-data') return false;
    this._attentionStore.attend(
      cardPK,
      entry.n ?? '',
      'live-data',
      'recording',
    );
    return true;
  }

  /** The trial card this user saved in the lesson since the recording started, if any. */
  private _trialSavedSinceRecording(): {pK: string; name: string} | undefined {
    const userId = this._userStore.state()?.userId;
    const lessonPK = this._routeStore.state().containerId;
    if (!userId || !lessonPK || !this._recordingStartedAt) return undefined;
    const entryState = this._entryStore.state();
    const trial = LSU.retrieveAndSortLessonLikeEntries(
      lessonPK as K.LabReportKey,
      entryState,
    )
      .map((item) => ({pK: item.pK, e: entryState[item.pK]?.e}))
      .filter(
        ({e}) =>
          e?.t === PL2.EntryType.TrialSection &&
          e.uId === userId &&
          Number(e.cAt) >= this._recordingStartedAt,
      )
      .pop();
    return trial ? {pK: trial.pK, name: trial.e?.n ?? ''} : undefined;
  }

  /** The open lesson chat's session, if the chat panel is open */
  private _openChatSessionId(): string | undefined {
    const routeState = this._routeStore.state();
    const userId = this._userStore.state()?.userId;
    if (!userId || !routeState.containerId || !isSessionPanelOpen(routeState)) {
      return undefined;
    }
    const sid = lessonChatSessionId(userId, routeState.containerId);
    return this._memoryStore.getSession(sid) ? sid : undefined;
  }

  private _sendAttention() {
    const sid = this._openChatSessionId();
    if (sid) this._sendAction.sendAttention(sid);
  }

  /** A status line in the open chat */
  private _note(type: CheckpointType) {
    const sid = this._openChatSessionId();
    if (!sid) return;
    const deviceName = this._connectionStore.state()?.deviceConfig?.name;
    this._memoryStore.addMessage(sid, {
      id: `${Date.now()}-checkpoint`,
      role: 'data',
      content: checkpointText(type, deviceName),
      isStreaming: false,
      statusIcon: CHECKPOINT_ICONS[type] ?? 'info',
      checkpoint: {type, timestamp: new Date().toISOString(), deviceName},
    });
  }

  /** Once per app session: a hello in the open chat after connecting, unless recording starts */
  private _scheduleWelcome() {
    if (this._welcomed) return;
    clearTimeout(this._welcomeTimer);
    this._welcomeTimer = setTimeout(() => {
      const sid = this._openChatSessionId();
      if (this._welcomed || !sid) return;
      this._welcomed = true;
      const deviceName =
        this._connectionStore.state()?.deviceConfig?.name ?? 'your sensor';
      const graphNames = [
        ...new Set(
          Object.values(this._dataConfigStore.state() ?? {})
            .map((dvc) => dvc?.n)
            .filter((n): n is string => !!n),
        ),
      ];
      const graphPart = graphNames.length
        ? `, showing **${graphNames.join(', ')}**`
        : '';
      const viewingRecording =
        DCSU.dataStatus(this._dataConfigStore.state() ?? {}) ===
        PL2.DataStatus.Recorded;
      const lead = viewingRecording
        ? `Viewing a recording from **${deviceName}**`
        : `Connected to **${deviceName}**`;
      this._memoryStore.addMessage(sid, {
        id: `${Date.now()}-unprompted`,
        role: 'assistant',
        content: `${lead}${graphPart}. What would you like to work on?`,
        isStreaming: false,
        quickReplies: WELCOME_QUICK_REPLIES,
      });
    }, WELCOME_DELAY_MS);
  }
}
